import LayoutEntityAvailability from '@models/LayoutEntityAvailability';
import { MenuItemParams } from '@models/MenuItem';

import { BaseContentType } from './Content';
import { MultiLangText } from './MultiLangText';
import { TOrderProvidersBy } from './OrderProvidersBy';
import { ServiceType } from './Provider';

/**
 * DO NOT CHANGE this value as it is used to locate default see all image!
 */
export const SEE_ALL = 'see-all';

export enum BehaviorScope {
  LINK,
  SEE_ALL,
}

export const SourceBehaviors = {
  [BehaviorScope.LINK]: ['standard', 'external-streaming-service'],
  [BehaviorScope.SEE_ALL]: [SEE_ALL],
} as const;

export type SourceBehavior =
  | (typeof SourceBehaviors)[BehaviorScope.LINK][number]
  | (typeof SourceBehaviors)[BehaviorScope.SEE_ALL][number];

export type PageSectionSourceParams = Omit<MenuItemParams, 'behavior'> & {
  behavior?: SourceBehavior;
  isPromo?: string;
  orderProvidersById?: string;
  orderProvidersByType?: ServiceType;
};

export const SOURCE_INSTANCE_ID = 'SOURCE_INSTANCE_ID';

export type PageSectionSource = {
  /**
   * Timestamp that specifies when the source becomes available on clients (if applicable). If omitted, the source will
   * be available "forever".
   */
  end?: null | string;
  /**
   * Image location (without aspect ratio)
   * eg.: `images/editorial/123`
   */
  image?: string;
  /**
   * Applies only to collections
   */
  limit?: number;
  /**
   * Applies to pages
   */
  name?: MultiLangText;
  /**
   * Any additional parameters (applies to links, unmanaged items and pages)
   */
  params?: PageSectionSourceParams;
  /**
   * Frontend-only ID that identifies this source occurrence inside editable lists. `sourceId`
   * identifies the referenced entity and can intentionally be duplicated.
   */
  [SOURCE_INSTANCE_ID]?: string;
  /**
   * Defines the ID of the source:
   * * For menu source: Menu ID
   * * For page source: Page ID
   * * For collection source: Collection ID
   * * For title source: Master ID
   * * Otherwise, UUID
   */
  sourceId: string;
  /**
   * Timestamp that specifies when the source becomes available on clients (if applicable). If omitted, the source will
   * become available immediately.
   */
  start?: null | string;
  type: BaseContentType;
};

export enum SourceFieldValue {
  DISABLED,
  OPTIONAL,
  REQUIRED,
}

type SourceFieldDefs = {
  flattened: boolean;
  image: SourceFieldValue;
  indexName: SourceFieldValue;
  itemReference: boolean;
  limit: SourceFieldValue;
  name: SourceFieldValue;
  params: {
    behavior: boolean;
    linkFields: boolean;
    orderProvidersBy: boolean;
    unmanagedField: boolean;
  };
};

export const PageSectionSourceDefs: Record<BaseContentType, SourceFieldDefs> = {
  collection: {
    flattened: true,
    image: SourceFieldValue.DISABLED,
    indexName: SourceFieldValue.DISABLED,
    itemReference: true,
    limit: SourceFieldValue.OPTIONAL,
    name: SourceFieldValue.OPTIONAL,
    params: {
      behavior: false,
      linkFields: false,
      orderProvidersBy: false,
      unmanagedField: false,
    },
  },
  image: {
    flattened: false,
    image: SourceFieldValue.REQUIRED,
    indexName: SourceFieldValue.DISABLED,
    itemReference: false,
    limit: SourceFieldValue.DISABLED,
    name: SourceFieldValue.OPTIONAL,
    params: {
      behavior: false,
      linkFields: false,
      orderProvidersBy: false,
      unmanagedField: false,
    },
  },
  link: {
    flattened: false,
    image: SourceFieldValue.OPTIONAL,
    name: SourceFieldValue.OPTIONAL,
    indexName: SourceFieldValue.DISABLED,
    limit: SourceFieldValue.DISABLED,
    itemReference: false,
    params: {
      linkFields: true,
      behavior: false,
      orderProvidersBy: false,
      unmanagedField: false,
    },
  },
  menu: {
    flattened: true,
    image: SourceFieldValue.DISABLED,
    name: SourceFieldValue.DISABLED,
    indexName: SourceFieldValue.DISABLED,
    limit: SourceFieldValue.DISABLED,
    itemReference: true,
    params: {
      linkFields: false,
      behavior: false,
      orderProvidersBy: false,
      unmanagedField: false,
    },
  },
  page: {
    flattened: false,
    image: SourceFieldValue.OPTIONAL,
    name: SourceFieldValue.OPTIONAL,
    indexName: SourceFieldValue.DISABLED,
    limit: SourceFieldValue.DISABLED,
    itemReference: true,
    params: {
      linkFields: false,
      behavior: true,
      orderProvidersBy: false,
      unmanagedField: false,
    },
  },
  title: {
    flattened: false,
    image: SourceFieldValue.OPTIONAL,
    name: SourceFieldValue.OPTIONAL,
    indexName: SourceFieldValue.OPTIONAL,
    limit: SourceFieldValue.DISABLED,
    itemReference: true,
    params: {
      linkFields: false,
      behavior: false,
      orderProvidersBy: true,
      unmanagedField: false,
    },
  },
  unmanaged: {
    flattened: false,
    image: SourceFieldValue.OPTIONAL,
    name: SourceFieldValue.OPTIONAL,
    indexName: SourceFieldValue.OPTIONAL,
    limit: SourceFieldValue.DISABLED,
    itemReference: false,
    params: {
      linkFields: false,
      behavior: true,
      orderProvidersBy: false,
      unmanagedField: true,
    },
  },
};

export const RegularSectionStyleTypes = [
  'swimlane',
  'swimlane-with-background',
  'swimlane-with-background-and-left-text',
  'carousel',
  'live-programs',
  'user-channels-ongoing',
  'user-channels-catchup',
  'continue-watching',
  'my-recordings',
  'bought-and-rented',
  'bought',
  'rented',
  'apps',
  'favorited-channels',
  'favorited-content',
  'sortable-grid',
  'plain-grid',
] as const;

export type RegularSectionStyleType = (typeof RegularSectionStyleTypes)[number];

export const DecoratedSwimlaneTypes = [
  'swimlane-with-background',
  'swimlane-with-background-and-left-text',
] satisfies RegularSectionStyleType[];

export type DecoratedSwimlaneType = (typeof DecoratedSwimlaneTypes)[number];

export const isDecoratedSwimlaneType = (type: SectionStyleType): type is DecoratedSwimlaneType =>
  DecoratedSwimlaneTypes.includes(type as DecoratedSwimlaneType);

export const StaticSectionStyleTypes = ['streaming-services-footer'] as const;
export type StaticSectionStyleType = (typeof StaticSectionStyleTypes)[number];

export type SectionStyleType = RegularSectionStyleType | StaticSectionStyleType;

export const SectionStyleSegments = [
  'wide',
  'wide-airtime',
  'poster',
  'channel-ongoing',
  'channel',
  'streaming-service',
  'banner-fullscreen',
  'promo-wide',
  'promo-wide-large',
  'genre',
  'app',
  'person',
  'kids-service',
  'poster-notext',
  'wide-notext-large',
  'wide-notext-double',
] as const;

export type SectionStyleSegment = (typeof SectionStyleSegments)[number];

export type PageSectionStyle = {
  // The following properties are only relevant for `swimlane-with-background` and `swimlane-with-background-and-left-text`
  backgroundImage?: string;
  descriptionColor?: string;
  hasHiddenLabel: boolean;
  labelColor?: string;

  pattern: string;
  repeatLastSegmentInPattern: boolean;
  type: SectionStyleType;
};

export type MinifiedPageSection = LayoutEntityAvailability & {
  // The following properties are only relevant for `swimlane-with-background` and `swimlane-with-background-and-left-text`
  description?: MultiLangText;
  id: string;
  label: MultiLangText;
  link: string;
  linkText?: MultiLangText;
  orderProvidersBy?: TOrderProvidersBy;

  slug: string;
  style: PageSectionStyle;
};

type PageSection = MinifiedPageSection & {
  sources: PageSectionSource[];
};

export default PageSection;
